import { FGCBanner } from "@components/FGC/FGCBanner";
import { EmbedModeContext } from "@components/donate/DonateV3/EmbedModeContext";
import { FaqComponent } from "@components/donate/DonateV3/FAQ";
import { Footer } from "@components/donate/DonateV3/Footer";
import { FundraiserLink } from "@components/donate/DonateV3/FundraiserLink";
import { GiftCardLink } from "@components/donate/DonateV3/GiftCardLink";
import { Header } from "@components/donate/DonateV3/Header";
import {
  NonprofitInfo,
  NonprofitInfoHeaderSmallScreen,
} from "@components/donate/DonateV3/NonprofitInfo";
import PaymentProcessWrapper from "@components/donate/DonateV3/PaymentProcess";
import {
  PaymentProcessRouteName,
  paymentProcessRouteNameToPathMap,
} from "@components/donate/DonateV3/PaymentProcess/components/PaymentProcessLink";
import {
  mergeDonateFormInitialValues,
  useDonateFlowCustomization,
} from "@components/donate/DonateV3/PaymentProcess/useDonateFlowCustomization";
import { useDonateForm } from "@components/donate/DonateV3/PaymentProcess/useDonateForm";
import { useDonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import { useValidValuesFromSearchParams } from "@components/donate/DonateV3/PaymentProcess/validators";
import { PromoBanner } from "@components/donate/DonateV3/PromoBanner";
import {
  DonateModalAction,
  DonateV3Layout,
  DonateV3PageProps,
} from "@components/donate/DonateV3/types";
import {
  DonateFormSearchValues,
  useDonateFormSearchParams,
} from "@components/donate/DonateV3/useDonateFormSearchParams";
import { useGridCardsDisplayController } from "@components/donate/DonateV3/useGridCardsDisplayController";
import { css } from "@emotion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  HashRouter,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";

import { DonationFlowCustomizationResponse } from "@every.org/common/src/codecs/donationFlowCustomization";
import { FundraiserResponse } from "@every.org/common/src/codecs/entities";
import { Currency } from "@every.org/common/src/entity/types";
import { CustomEvent } from "@every.org/common/src/helpers/analytics";
import {
  DonateModalUrlParams,
  DONATE_HASH,
} from "@every.org/common/src/helpers/clientRoutes";
import { searchParamToBool } from "@every.org/common/src/helpers/string";

import { Loading } from "src/components/LoadingIndicator";
import { useLoggedInOrGuestUserOrUndefined } from "src/context/AuthContext/hooks";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { useFallGivingChallengeActive } from "src/hooks/useFallGivingChallengeActive";
import { useNonprofitAnnouncement } from "src/hooks/useNonprofitAnnouncement";
import { Grid } from "src/pages/DonateV3/styles";
import {
  fetchFundraiser,
  FundraiserFetchStatus,
} from "src/pages/Fundraiser/fetchFundraiser";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { useUserLayer } from "src/utility/abtesting";
import { trackEvent } from "src/utility/analytics";
import { logger } from "src/utility/logger";
import { getWindow } from "src/utility/window";

let hasTrackedDonationModal = false;
// Here to only track the type once instead of on every render, but it
// also means that if you open many different modals in a single session
// you will not see this again. Reconsider after launching donate v3
function trackDonationModal(
  layout: DonateV3Layout,
  data?: Record<string, unknown>
) {
  if (!hasTrackedDonationModal) {
    // TODO consider adding type DonationModalType like old flow had
    trackEvent(CustomEvent.DONATE_SHOW_MODAL, { version: "V3", layout });
    hasTrackedDonationModal = true;
  }
  if (data?.donateAction === DonateModalAction.DONATE && data?.userId) {
    logger.info({ message: "DONATE_MODAL_LOADED", data });
  }
}

const DonateV3 = ({
  layout,
  ...props
}: DonateV3PageProps & {
  layout: DonateV3Layout;
}) => {
  const edoRouter = useEdoRouter();

  const searchParams = new URLSearchParams(edoRouter.search);

  // The embedded flow has no page behind it to exit to.
  const noExit =
    searchParamToBool(searchParams.get(DonateModalUrlParams.NO_EXIT)) ||
    !!props.embedMode;

  const { nonprofit, fromFundraiserId } = props;

  const initialValuesFromUrl = useDonateFormSearchParams();

  const fundraiserId = fromFundraiserId || initialValuesFromUrl.fundraiserId;

  const donateFlowCustomization = useDonateFlowCustomization({
    nonprofitId: nonprofit.id,
    fundraiserId,
    customizationCode: initialValuesFromUrl.customizationCode,
  });

  const mergedInitialValuesFromSearchParams = useMemo(
    () =>
      mergeDonateFormInitialValues({
        urlValues: initialValuesFromUrl,
        customization: donateFlowCustomization.customization,
        currency: props.minDonationValue?.currency || Currency.USD,
      }),
    [
      donateFlowCustomization.customization,
      initialValuesFromUrl,
      props.minDonationValue?.currency,
    ]
  );

  const initialValuesFromSearchParams = useValidValuesFromSearchParams(
    mergedInitialValuesFromSearchParams,
    {
      minDonationValue: props.minDonationValue,
    }
  );

  const loggedInOrGuestUser = useLoggedInOrGuestUserOrUndefined();
  useEffect(
    () =>
      trackDonationModal(layout, {
        nonprofitId: nonprofit.id,
        fundraiserId,
        donateAction: props.donateAction,
        matchCampaignId:
          props.donateAction === DonateModalAction.DONATE &&
          props.matchCampaign?.id,
        userId: loggedInOrGuestUser?.id,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      layout,
      nonprofit.id,
      fundraiserId,
      loggedInOrGuestUser?.id,
      props.donateAction,
    ]
  );

  const [fundraiser, setFundraiser] = useState<FundraiserResponse>();
  useAsyncEffect({
    asyncOperation: useCallback(() => {
      return fundraiserId
        ? fetchFundraiser(fundraiserId, nonprofit.id)
        : Promise.resolve(undefined);
    }, [fundraiserId, nonprofit.id]),
    handleResponse: useCallback(
      (
        result?: FundraiserResponse | FundraiserFetchStatus.FUNDRAISER_NOT_FOUND
      ) => {
        if (result && result !== FundraiserFetchStatus.FUNDRAISER_NOT_FOUND) {
          setFundraiser(result);
        }
      },
      []
    ),
    handleError: useCallback((e: Error) => {
      logger.error({
        message: "An error occurred fetching fundraiser for donate modal.",
        error: e,
      });
    }, []),
  });

  if (donateFlowCustomization.isLoading) {
    return (
      <Grid layout={layout}>
        <div css={{ gridArea: "paymentProcess" }}>
          <Loading />
        </div>
      </Grid>
    );
  }

  return (
    <DonateV3Initialized
      layout={layout}
      donateProps={props}
      fundraiser={fundraiser}
      initialValuesFromSearchParams={initialValuesFromSearchParams}
      noExit={noExit}
      customization={donateFlowCustomization.customization}
    />
  );
};

const DonateV3Initialized = ({
  layout,
  donateProps,
  fundraiser,
  initialValuesFromSearchParams,
  noExit,
  customization,
}: {
  layout: DonateV3Layout;
  donateProps: DonateV3PageProps;
  fundraiser?: FundraiserResponse;
  initialValuesFromSearchParams: DonateFormSearchValues;
  noExit: boolean;
  customization?: DonationFlowCustomizationResponse;
}) => {
  const { nonprofit } = donateProps;

  const form = useDonateForm({
    ...donateProps,
    initialValuesFromSearchParams,
  });
  const formContext = useDonateFormContext({
    modalProps: donateProps,
    form,
    initialValuesFromSearchParams,
    customization,
  });

  const gridCards = useGridCardsDisplayController({
    nonprofit,
    initialValuesFromSearchParams,
  });
  const showGiftCardLink = gridCards.showGiftCardLink && !formContext.embedMode;
  const showFundraiserLink =
    gridCards.showFundraiserLink && !formContext.embedMode;

  const showPromoBanners = !!useNonprofitAnnouncement(nonprofit);
  const navigate = useNavigate();

  // If the hash-route does not start with a "/", then replace with the
  // hash-prefixed version.
  const location = useLocation();
  useEffect(() => {
    if (!location.pathname.startsWith("/")) {
      navigate(`/${location.pathname}${location.search}${location.hash}`, {
        replace: true,
      });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  const isThankYouPage = useMatch(
    paymentProcessRouteNameToPathMap[PaymentProcessRouteName.THANK_YOU]
  );

  const { displayFGC, FGCDepleted } = useFallGivingChallengeActive(
    nonprofit.id
  );

  const showFGCBanner = displayFGC && !isThankYouPage;

  useUserLayer("a_a_test", false);

  const footer = (
    <Footer
      nonprofit={nonprofit}
      fundraiser={fundraiser}
      paymentOption={formContext.currentPaymentOption}
    />
  );

  return (
    <Grid
      layout={layout}
      css={[
        formContext.themeColor &&
          css`
            * {
              ${{
                [colorCssVars.accent.large]: formContext.themeColor,
                [colorCssVars.accent.largeHighlight]:
                  formContext.themeColorHighlight || formContext.themeColor,
                [colorCssVars.accent.smallHighlight]:
                  formContext.themeColorHighlight || formContext.themeColor,
                [colorCssVars.accent.small]: formContext.themeColor,
                [colorCssVars.input.border.focus]: formContext.themeColor,
                [colorCssVars.input.caret]: formContext.themeColor,
                [colorCssVars.input.outline.focus]:
                  formContext.themeColorHighlight || formContext.themeColor,
              }}
            }
          `,
        noExit &&
          cssForMediaSize({
            min: MediaSize.LARGE,
            css: { paddingTop: spacing.l },
          }),
      ]}
    >
      <div
        css={[
          { gridArea: "header" },
          cssForMediaSize({
            max: MediaSize.SMALL,
            css: { display: "flex", justifyContent: "space-between" },
          }),
        ]}
      >
        <NonprofitInfoHeaderSmallScreen
          nonprofit={nonprofit}
          fundraiser={fundraiser}
          formContext={formContext}
          customNonprofitDescription={
            initialValuesFromSearchParams.customNonprofitDescription
          }
        />
        {!noExit && (
          <Header {...donateProps} form={form} formContext={formContext} />
        )}
      </div>
      {(showPromoBanners || showFGCBanner) && (
        <div css={[{ gridArea: "promoBanners" }, verticalStackCss.m]}>
          {showFGCBanner && <FGCBanner depleted={FGCDepleted} />}
          {showPromoBanners && <PromoBanner nonprofit={nonprofit} />}
        </div>
      )}
      <div
        css={[
          { gridArea: "nonprofitInfo" },
          isThankYouPage &&
            cssForMediaSize({
              max: MediaSize.MEDIUM_LARGE,
              css: { display: "none" },
            }),
          cssForMediaSize({
            max: MediaSize.SMALL,
            css: { display: "none" },
          }),
        ]}
      >
        <NonprofitInfo
          nonprofit={nonprofit}
          fundraiser={fundraiser}
          formContext={formContext}
          customNonprofitDescription={
            initialValuesFromSearchParams.customNonprofitDescription
          }
        />
      </div>
      <div css={{ gridArea: "paymentProcess" }}>
        <PaymentProcessWrapper
          {...donateProps}
          form={form}
          formContext={formContext}
          fundraiser={fundraiser}
        />
        <div
          css={[
            { display: "none", marginTop: spacing.l },
            cssForMediaSize({
              min: MediaSize.LARGE,
              css: { display: "block" },
            }),
          ]}
        >
          {footer}
        </div>
      </div>
      <div
        css={[
          { gridArea: "fundraiserLink" },
          cssForMediaSize({
            max: MediaSize.MEDIUM_LARGE,
            css: "display: none",
          }),
        ]}
      >
        {showFundraiserLink && <FundraiserLink nonprofit={nonprofit} />}
      </div>
      <div css={{ gridArea: "giftCard" }}>
        {showGiftCardLink && <GiftCardLink />}
      </div>
      <div css={{ gridArea: "faq" }}>
        <FaqComponent
          nonprofit={nonprofit}
          fundraiser={fundraiser}
          paymentOption={formContext.currentPaymentOption}
          coverCostsMode={formContext.isCoverCostsMode}
          isInternational={formContext.isInternational}
          showStartFundraiserLink={showFundraiserLink}
        />
      </div>
      <div
        css={[
          { gridArea: "footer" },
          cssForMediaSize({ min: MediaSize.LARGE, css: { display: "none" } }),
        ]}
      >
        {footer}
      </div>
    </Grid>
  );
};

const DonateV3Wrapper = (
  props: DonateV3PageProps & { layout: DonateV3Layout }
) => {
  const router = useEdoRouter();

  // Likely unnecessary since this component shouldn't render if the proper hash
  // isn't set, but doesn't hurt to have it.
  const window = getWindow();
  useEffect(() => {
    if (window && !router.hash) {
      window.location.hash = DONATE_HASH;
      window.document.getElementById("loading-overlay")?.remove();
    }
  }, [router.hash, window]);

  return (
    <HashRouter>
      <EmbedModeContext.Provider value={!!props.embedMode}>
        <DonateV3 {...props} />
      </EmbedModeContext.Provider>
    </HashRouter>
  );
};

export default DonateV3Wrapper;
